import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = 'https://api.hayatfamily.app/api';

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

function DeviceSetupPage() {
  const [deviceId, setDeviceId] = useState(null);
  const [deviceUuid, setDeviceUuid] = useState(null);
  const [pairingCode, setPairingCode] = useState(null);
  const [status, setStatus] = useState('initializing');
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  
  const navigate = useNavigate();
  const pollingRef = useRef(null);
  const isInitializing = useRef(false);

  // Generate or retrieve device UUID
  const getDeviceUuid = () => {
    let uuid = localStorage.getItem('hayat_device_uuid');
    if (!uuid) {
      uuid = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('hayat_device_uuid', uuid);
      console.log('[Device] Generated new UUID:', uuid);
    } else {
      console.log('[Device] Using existing UUID:', uuid);
    }
    return uuid;
  };

  // Detect platform
  const detectPlatform = () => {
    const ua = navigator.userAgent;
    if (/iPad|iPhone|iPod/.test(ua)) return 'ios_web';
    if (/Android/.test(ua)) return 'android_web';
    return 'other';
  };

  useEffect(() => {
    if (!isInitializing.current) {
      isInitializing.current = true;
      initializeDevice();
    }
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  const initializeDevice = async () => {
    console.log('[Device] Starting initialization');
    
    try {
      const uuid = getDeviceUuid();
      setDeviceUuid(uuid);
      
      // Register device
      await registerDevice(uuid);
      
    } catch (err) {
      console.error('[Device] Initialization error:', err);
      handleInitError(err);
    }
  };

  const registerDevice = async (uuid) => {
    try {
      console.log('[Device] POST /devices/register-uuid');
      const response = await axiosInstance.post('/devices/register-uuid', {
        device_uuid: uuid,
        platform: detectPlatform(),
        app_version: '2.0'
      });
      
      console.log('[Device] Registration response:', response.data);
      
      const data = response.data;
      const newDeviceId = data.device_id;
      const newPairingCode = data.pairing_code;
      const deviceStatus = data.status;
      
      if (!newDeviceId) {
        throw new Error('No device_id in response');
      }
      
      setDeviceId(newDeviceId);
      localStorage.setItem('hayat_device_id', newDeviceId);
      
      if (deviceStatus === 'paired') {
        console.log('[Device] Already paired');
        console.log(`[Device] Using device_id: ${newDeviceId}`);
        setStatus('paired');
        setTimeout(() => navigate(`/device/${newDeviceId}`), 2000);
        return;
      }
      
      if (newPairingCode) {
        setPairingCode(newPairingCode);
      }
      
      setStatus('waiting');
      startPolling(newDeviceId);
      
    } catch (err) {
      console.error('[Device] Registration failed:', err);
      throw err;
    }
  };

  const startPolling = (deviceId) => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }
    
    console.log('[Device] Starting polling for device_id:', deviceId);
    
    const poll = async () => {
      try {
        const response = await axiosInstance.get(`/devices/${deviceId}/pairing-status`);
        console.log('[Device] Polling status:', response.data);
        
        if (response.data.paired === true || response.data.status === 'paired') {
          console.log('[Device] Device paired!');
          console.log(`[Device] Using device_id: ${deviceId}`);
          clearInterval(pollingRef.current);
          setStatus('paired');
          
          if (response.data.household_id) {
            localStorage.setItem('hayat_household_id', response.data.household_id);
          }
          if (response.data.senior_id) {
            localStorage.setItem('hayat_senior_id', response.data.senior_id);
          }
          
          setTimeout(() => navigate(`/device/${deviceId}`), 2000);
        }
      } catch (err) {
        if (err.response?.status === 404) {
          console.error('[Device] Device not found (404) - clearing and restarting');
          clearInterval(pollingRef.current);
          localStorage.removeItem('hayat_device_id');
          window.location.reload();
        } else {
          console.error('[Device] Polling error:', err);
        }
      }
    };
    
    poll();
    pollingRef.current = setInterval(poll, 5000);
  };

  const handleInitError = (err) => {
    const nextRetry = retryCount + 1;
    setRetryCount(nextRetry);
    
    if (nextRetry < 3) {
      const delay = Math.min(1000 * Math.pow(2, nextRetry), 5000);
      console.log(`[Device] Retry ${nextRetry}/3 in ${delay}ms`);
      setStatus('retrying');
      setTimeout(() => {
        isInitializing.current = false;
        initializeDevice();
      }, delay);
    } else {
      console.error('[Device] Max retries reached');
      setStatus('error');
      setError(err.message || 'Initialisierung fehlgeschlagen');
    }
  };

  const handleFactoryReset = () => {
    if (window.confirm('Gerät zurücksetzen? Alle Daten werden gelöscht.')) {
      console.log('[Device] Factory reset initiated');
      localStorage.removeItem('hayat_device_uuid');
      localStorage.removeItem('hayat_device_id');
      localStorage.removeItem('hayat_household_id');
      localStorage.removeItem('hayat_senior_id');
      window.location.reload();
    }
  };

  if (status === 'initializing' || status === 'retrying') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Gerät wird initialisiert...</h2>
          <p className="text-gray-600">Bitte warten</p>
          {status === 'retrying' && retryCount > 0 && (
            <p className="text-sm text-gray-500 mt-2">Versuch {retryCount + 1}/3</p>
          )}
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Fehler</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <div className="space-y-2">
            <button onClick={() => window.location.reload()} className="w-full px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
              Erneut versuchen
            </button>
            <button onClick={handleFactoryReset} className="w-full px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
              Gerät zurücksetzen
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'paired') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="text-green-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Gerät gekoppelt!</h2>
          <p className="text-gray-600">Verbindung hergestellt</p>
        </div>
      </div>
    );
  }

  const digits = pairingCode ? pairingCode.split('').filter(c => /\d/.test(c)) : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Hayat Family</h1>
          <p className="text-gray-600">Gerät koppeln</p>
        </div>

        <div className="mb-8">
          <h2 className="text-xl font-semibold text-center text-gray-700 mb-4">
            Kopplungs-Code:
          </h2>
          
          <div className="flex justify-center gap-2 mb-4">
            {digits.length >= 6 ? (
              digits.slice(0, 6).map((digit, i) => (
                <div key={i} className="w-16 h-20 bg-indigo-600 text-white text-4xl font-bold rounded-lg flex items-center justify-center shadow-lg">
                  {digit}
                </div>
              ))
            ) : (
              <div className="text-gray-500">Code wird geladen...</div>
            )}
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-gray-800 mb-2">Bitte Kopplungs-Code in der Familien-App eingeben</h3>
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
            <li>Öffnen Sie die Familien-App</li>
            <li>Melden Sie sich an</li>
            <li>Wählen Sie "Gerät hinzufügen"</li>
            <li>Geben Sie den Code ein</li>
          </ol>
        </div>

        <div className="text-center">
          <div className="inline-flex items-center gap-2 text-sm text-gray-500">
            <div className="animate-pulse w-2 h-2 bg-indigo-600 rounded-full"></div>
            Warte auf Kopplung...
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="text-xs text-gray-400 text-center space-y-1">
            {deviceId && <p>Geräte-ID: {deviceId.slice(0, 12)}...</p>}
            <button onClick={handleFactoryReset} className="text-red-500 hover:text-red-700 underline">
              Gerät zurücksetzen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DeviceSetupPage;
